import React, { useState } from "react";
import { Badge, Button, Card, Flex, Text } from "@radix-ui/themes";
import { useQueryClient } from "@tanstack/react-query";
import { PermissionAlertDialog } from "components";
import { useGetBloomreachStatus, useValidateBloomreach } from "hooks";
import { useToast } from "../../../context/ToastContext";
import { AuthBloomreachDialog } from "./AuthBloomreachDialog";
import { BloomreachChoiceDialog } from "./BloomreachChoiceDialog";
import { BloomreachMappingDialog } from "./BloomreachMappingDialog";

const EMPTY_FORM = {
  api_base_url: "https://api.exponea.com",
  project_token: "",
  api_key_id: "",
  api_secret: "",
};

export function BrowseBloomreachCard({ userProfile }) {
  const { setToastMessage } = useToast();
  const queryClient = useQueryClient();
  const validateBloomreach = useValidateBloomreach();
  const { data: statusData } = useGetBloomreachStatus(userProfile?.organisation_id);
  const isActive = statusData?.data?.is_active === true;

  const [authOpen, setAuthOpen] = useState(false);
  const [choiceOpen, setChoiceOpen] = useState(false);
  const [mappingOpen, setMappingOpen] = useState(false);
  const [permissionOpen, setPermissionOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [values, setValues] = useState(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [initialMappings, setInitialMappings] = useState([]);
  const [initialFieldMappings, setInitialFieldMappings] = useState(null);
  const [syncConsent, setSyncConsent] = useState(true);
  const [reconnectAction, setReconnectAction] = useState(null);

  const onChange = (key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => ({ ...prev, [key]: false }));
  };

  const validateForm = () => {
    const errors = {
      project_token: !values.project_token.trim(),
      api_key_id: !values.api_key_id.trim(),
      api_secret: !values.api_secret.trim(),
    };
    setFieldErrors(errors);
    return !Object.values(errors).some(Boolean);
  };

  const openMapping = (payload) => {
    setInitialMappings(payload?.location_mappings || []);
    setInitialFieldMappings(payload?.field_mappings || null);
    setSyncConsent(payload?.sync_consent !== false);
    setReconnectAction(payload?.action || null);
    setAuthOpen(false);
    setChoiceOpen(false);
    setMappingOpen(true);
  };

  const handleSave = async (choice = null) => {
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      const payload = {
        organisation_id: userProfile.organisation_id,
        api_base_url: values.api_base_url.trim() || "https://api.exponea.com",
        project_token: values.project_token.trim(),
        api_key_id: values.api_key_id.trim(),
        api_secret: values.api_secret.trim(),
      };
      if (choice) payload.action_choice = choice;

      const result = await validateBloomreach.mutateAsync(payload);
      const status = result?.data?.status;
      if (status === "pending_user_choice") {
        setChoiceOpen(true);
        return;
      }
      if (status === "success") {
        queryClient.invalidateQueries(["bloomreach-status"]);
        openMapping(result.data);
        return;
      }
      setToastMessage(result?.data?.message || "Could not validate Bloomreach credentials.");
    } catch (error) {
      setToastMessage(error?.response?.data?.message || "Could not validate Bloomreach credentials.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Card
        style={{
          display: "flex",
          minWidth: "455px",
          padding: "var(--space-4)",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Flex align="center" gap="4">
          <Flex
            height="40px"
            width="40px"
            p="2"
            style={{
              borderRadius: "var(--space-1)",
              background: "#FFFFFF",
              border: "1px solid var(--gray-a6)",
            }}
          >
            <img
              src={require("../../../assets/images/bloomreach.jpeg")}
              alt="Bloomreach"
              style={{ width: "24px" }}
            />
          </Flex>
          <Flex direction="column" justify="center" align="start">
            <Text size="4" weight="medium">Bloomreach</Text>
            <Text size="1" color="gray">Customer data platform</Text>
          </Flex>
        </Flex>
        {isActive ? (
          <Badge size="1" variant="soft" color="green">Connected</Badge>
        ) : (
          <Button
            size="2"
            variant="soft"
            color="gray"
            onClick={() =>
              userProfile?.user_role === "Owner" ? setAuthOpen(true) : setPermissionOpen(true)
            }
          >
            Connect
          </Button>
        )}
      </Card>

      <AuthBloomreachDialog
        isOpen={authOpen}
        onOpenChange={(open) => {
          setAuthOpen(open);
          if (!open) {
            setValues(EMPTY_FORM);
            setFieldErrors({});
          }
        }}
        onCancel={() => setAuthOpen(false)}
        onSave={handleSave}
        values={values}
        onChange={onChange}
        fieldErrors={fieldErrors}
        isLoading={isLoading}
      />
      <BloomreachChoiceDialog
        isOpen={choiceOpen}
        onOpenChange={setChoiceOpen}
        isLoading={isLoading}
        onChoice={(choice) => handleSave(choice)}
      />
      <BloomreachMappingDialog
        isOpen={mappingOpen}
        onOpenChange={setMappingOpen}
        initialMappings={initialMappings}
        initialFieldMappings={initialFieldMappings}
        initialSyncConsent={syncConsent}
        reconnectAction={reconnectAction}
      />
      <PermissionAlertDialog
        isOpen={permissionOpen}
        onOpenChange={setPermissionOpen}
        message="Only owners can perform this action"
      />
    </>
  );
}
